import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Loader2, Package, CheckCircle2 } from 'lucide-react';

const RequestTemplatesTab = ({ clientId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Field labels (customizable)
  const [fieldLabels, setFieldLabels] = useState({
    security_groups: "Security Groups",
    mailbox_access: "Email/Mailbox Access",
    printer_access: "Printer Access",
    software_access: "Software Access",
    phone_config: "Phone Configuration",
    network_access: "Network Access",
    shared_drives: "Shared Drives",
    other_provisions: "Other Provisions"
  });
  
  // Available options for each provision category (customizable)
  const [availableOptions, setAvailableOptions] = useState({
    security_groups: ["Domain Users", "VPN Users", "Remote Desktop Users", "Sales File Share", "Finance File Share", "HR File Share", "IT Admin"],
    mailbox_access: ["Standard Email", "Mobile Device Sync", "Shared Mailbox - Support", "Shared Mailbox - Sales", "Shared Mailbox - HR"],
    printer_options: ["None", "Office Printer - Floor 1", "Office Printer - Floor 2", "All Printers", "Secure Printer - Finance"],
    software_access: ["Office 365", "CRM Access", "QuickBooks", "Adobe Creative Cloud", "Development Tools", "Project Management Software"],
    network_access: ["VPN - Full Access", "VPN - Limited", "WiFi - Corporate", "WiFi - Guest"],
    shared_drives: ["Company Shared Drive - Read Only", "Company Shared Drive - Read/Write", "Department Drive - Full Access", "Project Folders"]
  });
  
  // Temp state for editing configuration
  const [tempConfig, setTempConfig] = useState({
    labels: {},
    options: {}
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'access',
    provisions: {
      security_groups: [],
      mailbox_access: [],
      printer_access: '',
      software_access: [],
      phone_config: '',
      network_access: [],
      shared_drives: [],
      other_provisions: ''
    },
    msp_notes: ''
  });

  useEffect(() => {
    fetchTemplates();
    loadConfiguration();
  }, [clientId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}/request-templates`);
      setTemplates(res.data.templates || []);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadConfiguration = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/template-config`);
      if (res.data.labels) {
        setFieldLabels(res.data.labels);
      }
      if (res.data.options) {
        setAvailableOptions(res.data.options);
      }
    } catch (error) {
      // Use defaults if no config exists yet
      console.log('No custom configuration found, using defaults');
    }
  };

  const saveConfiguration = async () => {
    try {
      await api.put(`/clients/${clientId}/template-config`, {
        labels: tempConfig.labels,
        options: tempConfig.options
      });
      setFieldLabels(tempConfig.labels);
      setAvailableOptions(tempConfig.options);
      setShowConfigDialog(false);
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const openConfigDialog = () => {
    setTempConfig({
      labels: { ...fieldLabels },
      options: { ...availableOptions }
    });
    setShowConfigDialog(true);
  };

  const updateTempLabel = (key, value) => {
    setTempConfig({
      ...tempConfig,
      labels: {
        ...tempConfig.labels,
        [key]: value
      }
    });
  };

  const updateTempOptions = (key, value) => {
    setTempConfig({
      ...tempConfig,
      options: {
        ...tempConfig.options,
        [key]: value.split('\n').filter(o => o.trim())
      }
    });
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Template name is required');
      return;
    }

    try {
      // Prepare the data with client_id
      const templateData = {
        ...formData,
        client_id: clientId
      };

      if (editingTemplate) {
        await api.put(`/clients/${clientId}/request-templates/${editingTemplate.id}`, templateData);
        toast.success('Template updated successfully');
      } else {
        await api.post(`/clients/${clientId}/request-templates`, templateData);
        toast.success('Template created successfully');
      }
      
      setShowDialog(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Template save error:', error);
      console.error('Error response:', error.response);
      
      // Handle Pydantic validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const validationErrors = error.response.data.detail.map(err => 
          `${err.loc?.join('.')} - ${err.msg}`
        ).join(', ');
        toast.error(`Validation error: ${validationErrors}`);
      } else {
        const errorMsg = error.response?.data?.detail || (editingTemplate ? 'Failed to update template' : 'Failed to create template');
        toast.error(errorMsg);
      }
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category || 'access',
      provisions: template.provisions || {
        security_groups: [],
        mailbox_access: [],
        printer_access: '',
        software_access: [],
        phone_config: '',
        network_access: [],
        shared_drives: [],
        other_provisions: ''
      },
      msp_notes: template.msp_notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/clients/${clientId}/request-templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openNewDialog = () => {
    setEditingTemplate(null);
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'access',
      provisions: {
        security_groups: [],
        mailbox_access: [],
        printer_access: '',
        software_access: [],
        phone_config: '',
        network_access: [],
        shared_drives: [],
        other_provisions: ''
      },
      msp_notes: ''
    });
  };

  const toggleArrayItem = (category, item) => {
    const currentItems = formData.provisions[category] || [];
    const newItems = currentItems.includes(item)
      ? currentItems.filter(i => i !== item)
      : [...currentItems, item];
    
    setFormData({
      ...formData,
      provisions: {
        ...formData.provisions,
        [category]: newItems
      }
    });
  };

  const updateProvision = (category, value) => {
    setFormData({
      ...formData,
      provisions: {
        ...formData.provisions,
        [category]: value
      }
    });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      general: 'bg-gray-100 text-gray-700',
      access: 'bg-blue-100 text-blue-700',
      software: 'bg-purple-100 text-purple-700',
      equipment: 'bg-green-100 text-green-700',
      support: 'bg-orange-100 text-orange-700'
    };
    
    return (
      <Badge className={`${colors[category] || colors.general} border-0`}>
        {category.toUpperCase()}
      </Badge>
    );
  };

  const renderProvisionSummary = (provisions) => {
    // Safety check for undefined provisions (old template format)
    if (!provisions || typeof provisions !== 'object') {
      return ['No provisions configured (legacy template)'];
    }
    
    const items = [];
    
    if (provisions.security_groups?.length > 0) {
      items.push(`Security: ${provisions.security_groups.join(', ')}`);
    }
    if (provisions.mailbox_access?.length > 0) {
      items.push(`Email: ${provisions.mailbox_access.join(', ')}`);
    }
    if (provisions.printer_access) {
      items.push(`Printer: ${provisions.printer_access}`);
    }
    if (provisions.software_access?.length > 0) {
      items.push(`Software: ${provisions.software_access.join(', ')}`);
    }
    if (provisions.network_access?.length > 0) {
      items.push(`Network: ${provisions.network_access.join(', ')}`);
    }
    
    return items.length > 0 ? items : ['No provisions configured'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Request Templates ({templates.length})</h3>
          <p className="text-sm text-gray-600">Pre-configured service packages for end users to request</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openConfigDialog} variant="outline" className="gap-2">
            <Edit className="w-4 h-4" />
            Configure Options
          </Button>
          <Button onClick={openNewDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            Create Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-4">Create service package templates that end users can request</p>
            <Button onClick={openNewDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      {getCategoryBadge(template.category)}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(template)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">What's Included:</p>
                    <div className="space-y-1">
                      {renderProvisionSummary(template.provisions).map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {template.msp_notes && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500">
                        <span className="font-medium">MSP Notes:</span> {template.msp_notes}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Service Package' : 'Create Service Package'}</DialogTitle>
            <DialogDescription>
              Configure a pre-set package that end users can request. You choose what's included.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Package Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., New User - Sales Team"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of what this package includes"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="access">Access Request</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Provisions Configuration */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium text-sm text-gray-900">Configure What's Included</h4>
              
              {/* Security Groups */}
              <div className="space-y-2">
                <Label>{fieldLabels.security_groups}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableOptions.security_groups.map((group) => (
                    <label key={group} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.provisions.security_groups?.includes(group)}
                        onChange={() => toggleArrayItem('security_groups', group)}
                        className="rounded"
                      />
                      <span>{group}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Mailbox Access */}
              <div className="space-y-2">
                <Label>{fieldLabels.mailbox_access}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableOptions.mailbox_access.map((access) => (
                    <label key={access} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.provisions.mailbox_access?.includes(access)}
                        onChange={() => toggleArrayItem('mailbox_access', access)}
                        className="rounded"
                      />
                      <span>{access}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Printer Access */}
              <div className="space-y-2">
                <Label>{fieldLabels.printer_access}</Label>
                <Select
                  value={formData.provisions.printer_access || ''}
                  onValueChange={(value) => updateProvision('printer_access', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select printer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableOptions.printer_options.map((printer) => (
                      <SelectItem key={printer} value={printer}>{printer}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Software Access */}
              <div className="space-y-2">
                <Label>{fieldLabels.software_access}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableOptions.software_access.map((software) => (
                    <label key={software} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.provisions.software_access?.includes(software)}
                        onChange={() => toggleArrayItem('software_access', software)}
                        className="rounded"
                      />
                      <span>{software}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Network Access */}
              <div className="space-y-2">
                <Label>{fieldLabels.network_access}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableOptions.network_access.map((network) => (
                    <label key={network} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.provisions.network_access?.includes(network)}
                        onChange={() => toggleArrayItem('network_access', network)}
                        className="rounded"
                      />
                      <span>{network}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Shared Drives */}
              <div className="space-y-2">
                <Label>{fieldLabels.shared_drives}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableOptions.shared_drives.map((drive) => (
                    <label key={drive} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.provisions.shared_drives?.includes(drive)}
                        onChange={() => toggleArrayItem('shared_drives', drive)}
                        className="rounded"
                      />
                      <span>{drive}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Phone Config */}
              <div className="space-y-2">
                <Label>{fieldLabels.phone_config}</Label>
                <Input
                  placeholder="e.g., Desk phone with voicemail extension"
                  value={formData.provisions.phone_config || ''}
                  onChange={(e) => updateProvision('phone_config', e.target.value)}
                />
              </div>

              {/* Other Provisions */}
              <div className="space-y-2">
                <Label>{fieldLabels.other_provisions}</Label>
                <Textarea
                  placeholder="Any other access or provisions to include..."
                  value={formData.provisions.other_provisions || ''}
                  onChange={(e) => updateProvision('other_provisions', e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            {/* MSP Notes */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="msp_notes">Internal MSP Notes</Label>
              <Textarea
                id="msp_notes"
                placeholder="Internal notes for your team (not visible to end users)"
                value={formData.msp_notes}
                onChange={(e) => setFormData({ ...formData, msp_notes: e.target.value })}
                rows={2}
              />
              <p className="text-xs text-gray-500">These notes are only visible to MSP staff</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingTemplate ? 'Update' : 'Create'} Package
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Field Labels & Options</DialogTitle>
            <DialogDescription>
              Customize field names and available options for your organization
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Security Groups */}
              <div className="space-y-3 border-r pr-4">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.security_groups || fieldLabels.security_groups}
                    onChange={(e) => updateTempLabel('security_groups', e.target.value)}
                    placeholder="Security Groups"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.security_groups?.join('\n') || availableOptions.security_groups.join('\n')}
                    onChange={(e) => updateTempOptions('security_groups', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Mailbox Access */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.mailbox_access || fieldLabels.mailbox_access}
                    onChange={(e) => updateTempLabel('mailbox_access', e.target.value)}
                    placeholder="Email/Mailbox Access"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.mailbox_access?.join('\n') || availableOptions.mailbox_access.join('\n')}
                    onChange={(e) => updateTempOptions('mailbox_access', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Printer Access */}
              <div className="space-y-3 border-r pr-4">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.printer_access || fieldLabels.printer_access}
                    onChange={(e) => updateTempLabel('printer_access', e.target.value)}
                    placeholder="Printer Access"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.printer_options?.join('\n') || availableOptions.printer_options.join('\n')}
                    onChange={(e) => updateTempOptions('printer_options', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Software Access */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.software_access || fieldLabels.software_access}
                    onChange={(e) => updateTempLabel('software_access', e.target.value)}
                    placeholder="Software Access"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.software_access?.join('\n') || availableOptions.software_access.join('\n')}
                    onChange={(e) => updateTempOptions('software_access', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Network Access */}
              <div className="space-y-3 border-r pr-4">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.network_access || fieldLabels.network_access}
                    onChange={(e) => updateTempLabel('network_access', e.target.value)}
                    placeholder="Network Access"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.network_access?.join('\n') || availableOptions.network_access.join('\n')}
                    onChange={(e) => updateTempOptions('network_access', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>

              {/* Shared Drives */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Field Label</Label>
                  <Input
                    value={tempConfig.labels?.shared_drives || fieldLabels.shared_drives}
                    onChange={(e) => updateTempLabel('shared_drives', e.target.value)}
                    placeholder="Shared Drives"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Available Options (one per line)</Label>
                  <Textarea
                    value={tempConfig.options?.shared_drives?.join('\n') || availableOptions.shared_drives.join('\n')}
                    onChange={(e) => updateTempOptions('shared_drives', e.target.value)}
                    rows={6}
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveConfiguration}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestTemplatesTab;
